import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Compass, ShieldCheck, Sparkles, Heart, Flame, Network } from 'lucide-react';

const HERO_IMAGE_URL =
  'https://customer-assets.emergentagent.com/job_e5d93115-bd66-4394-96e8-5a28b2b40b8f/artifacts/d8brl0om_Rhys_Sterling_a_person_standing_slightly_apart_in_a_casual_so_027b2222-5110-4762-a4d5-76e55c0223f2_3.png';

const PATHWAYS = [
  {
    id: 'connection',
    n: '01',
    icon: Compass,
    headline: "I'm not sure what I want.",
    description: 'Figure out what kind of connection actually makes sense for you right now.',
    cta: 'Start Connection Discovery',
    accent: 'gold',
    available: true,
  },
  {
    id: 'readiness',
    n: '02',
    icon: ShieldCheck,
    headline: "I don't know if I'm actually ready.",
    description: "See if your expectations, energy, and availability match what you say you want.",
    cta: 'Check Connection Readiness',
    accent: 'teal',
    available: true,
  },
  {
    id: 'attraction-alignment',
    n: '03',
    icon: Sparkles,
    headline: 'Maybe my type isn\'t actually my type.',
    description: "See whether what you're drawn to actually lines up with what you say you want.",
    cta: 'Start Chemistry Check',
    accent: 'navy',
    available: true,
  },
  {
    id: 'kinsey',
    n: '04',
    icon: Heart,
    headline: "I'm questioning who I'm attracted to.",
    description: 'See your attraction patterns and how they may have changed over time.',
    cta: 'Use Kinsey Estimator',
    accent: 'teal',
    available: true,
  },
  {
    id: 'haevn',
    n: '05',
    icon: Flame,
    headline: 'Something feels off sexually.',
    description: "See what's working, what isn't, and what may be missing.",
    cta: 'Check Sexual Satisfaction',
    accent: 'navy',
    available: true,
  },
  {
    id: 'relationship-dna',
    n: '06',
    icon: Network,
    headline: "My situation isn't exactly traditional.",
    description: 'Figure out what kind of relationship structure actually makes sense for you.',
    cta: 'Explore Relationship DNA',
    accent: 'gold',
    available: false,
  },
];

const accentColor = (accent) =>
  accent === 'teal'
    ? 'hsl(var(--teal))'
    : accent === 'gold'
    ? 'hsl(var(--gold))'
    : 'hsl(var(--primary))';

const accentBg = (accent) =>
  accent === 'teal'
    ? 'hsl(var(--teal) / 0.08)'
    : accent === 'gold'
    ? 'hsl(var(--gold) / 0.08)'
    : 'hsl(var(--primary) / 0.08)';

const accentGlow = (accent) =>
  accent === 'teal'
    ? '0 18px 40px -18px hsl(var(--teal) / 0.35)'
    : accent === 'gold'
    ? '0 18px 40px -18px hsl(var(--gold) / 0.45)'
    : '0 18px 40px -18px hsl(var(--primary) / 0.35)';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const PathwayCard = ({ pathway, index, onSelect }) => {
  const Icon = pathway.icon;
  const color = accentColor(pathway.accent);
  const bg = accentBg(pathway.accent);
  const glow = accentGlow(pathway.accent);
  const isDisabled = !pathway.available;

  return (
    <motion.div
      {...fade(0.18 + index * 0.05)}
      whileHover={isDisabled ? undefined : { y: -4 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="h-full"
    >
      <Card
        onClick={() => !isDisabled && onSelect(pathway.id)}
        className={`group relative h-full overflow-hidden border-t border-r border-b border-border bg-card transition-all duration-300 ${
          isDisabled
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer hover:shadow-xl'
        }`}
        style={{
          borderLeftWidth: '3px',
          borderLeftStyle: 'solid',
          borderLeftColor: color,
          boxShadow: isDisabled ? undefined : `0 1px 2px hsl(var(--foreground) / 0.04)`,
        }}
        onMouseEnter={(e) => {
          if (!isDisabled) e.currentTarget.style.boxShadow = glow;
        }}
        onMouseLeave={(e) => {
          if (!isDisabled) e.currentTarget.style.boxShadow = `0 1px 2px hsl(var(--foreground) / 0.04)`;
        }}
        data-testid={`pathway-card-${pathway.id}`}
      >
        {/* Decorative accent wash (top-right) */}
        <div
          className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl"
          style={{ backgroundColor: bg }}
        />

        <CardContent className="relative p-7 sm:p-8 flex flex-col h-full">
          {/* Top row: large serif number + icon */}
          <div className="flex items-start justify-between mb-4">
            <span
              className="font-serif italic text-3xl sm:text-4xl leading-none"
              style={{ color: color, opacity: 0.85 }}
              data-testid={`pathway-number-${pathway.id}`}
            >
              {pathway.n}
            </span>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
              style={{ backgroundColor: bg, color: color }}
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
            </div>
          </div>

          {/* Headline — very large serif */}
          <h3
            className="font-serif text-[1.9rem] sm:text-[2.15rem] lg:text-[2.35rem] font-medium text-foreground leading-[1.1] tracking-tight mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {pathway.headline}
          </h3>

          {/* Description */}
          <p className="text-sm sm:text-[0.95rem] text-muted-foreground leading-relaxed mb-7 flex-1">
            {pathway.description}
          </p>

          {/* CTA */}
          {isDisabled ? (
            <div className="flex items-center justify-between pt-4 border-t border-border/40">
              <span className="text-xs font-medium tracking-wide text-muted-foreground/70">
                {pathway.cta}
              </span>
              <span
                className="text-[10px] font-medium tracking-[0.15em] uppercase px-2 py-1 rounded-full"
                style={{
                  backgroundColor: 'hsl(var(--muted))',
                  color: 'hsl(var(--muted-foreground))',
                }}
                data-testid={`pathway-coming-soon-${pathway.id}`}
              >
                Coming soon
              </span>
            </div>
          ) : (
            <div
              className="flex items-center justify-between pt-4 border-t border-border/40 group-hover:border-transparent transition-colors"
              data-testid={`pathway-cta-${pathway.id}`}
            >
              <span
                className="text-sm font-medium tracking-tight transition-colors"
                style={{ color }}
              >
                {pathway.cta}
              </span>
              <ArrowRight
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                style={{ color }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const SelfDiscoveryHub = ({ onSelectTool }) => {
  return (
    <div className="min-h-screen relative" data-testid="self-discovery-hub">
      {/* Editorial hero — image as backdrop, copy overlaid left */}
      <section
        className="relative w-full overflow-hidden flex flex-col"
        style={{ minHeight: 'clamp(540px, 60vh, 580px)' }}
      >
        {/* Background image — face shifted on mobile so text sits below it */}
        <img
          src={HERO_IMAGE_URL}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-[60%_15%] md:object-[70%_22%]"
          style={{
            filter: 'saturate(0.92) contrast(0.96)',
          }}
        />

        {/* Subtle dark overlay for text legibility — image stays visible */}
        <div
          className="absolute inset-0 pointer-events-none bg-black/20"
          aria-hidden="true"
        />

        {/* Mobile-only: extra darken at the bottom where text sits */}
        <div
          className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none md:hidden"
          aria-hidden="true"
          style={{
            background:
              'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0.55) 100%)',
          }}
        />

        {/* Soft warm haze */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-50"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(ellipse 50% 60% at 75% 45%, hsl(var(--gold) / 0.45), transparent 70%)',
          }}
        />

        {/* Copy — sits at bottom of hero on mobile (below face), upper-left on desktop */}
        <div className="relative w-full mt-auto md:mt-0 px-6 pt-8 md:pt-32 lg:pt-36 pb-10 md:pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-xl lg:max-w-[560px]">
              <motion.h1
                {...fade(0.04)}
                className="font-serif text-[2.5rem] sm:text-5xl lg:text-[3.5rem] font-bold text-white tracking-tight leading-[1.05] mb-6"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  textShadow: '0 2px 24px rgba(0,0,0,0.5)',
                }}
                data-testid="hub-headline"
              >
                The Best Matches Start With Honesty and Clarity.
              </motion.h1>

              <motion.p
                {...fade(0.10)}
                className="text-base sm:text-lg text-white/90 font-light leading-relaxed mb-4"
                style={{ textShadow: '0 1px 16px rgba(0,0,0,0.55)' }}
                data-testid="hub-subheadline"
              >
                The better we understand what you actually want, the better your matches will be.
              </motion.p>

              <motion.p
                {...fade(0.16)}
                className="text-sm sm:text-base text-white/80 font-light leading-relaxed"
                style={{ textShadow: '0 1px 16px rgba(0,0,0,0.55)' }}
              >
                If there are still things you're unsure about, these tools can help.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Framing — breathing room + soft separator above */}
      <section className="px-6 pt-16 sm:pt-20 pb-8">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade(0.18)} className="mb-2">
            <div className="h-px w-12 bg-[hsl(var(--gold))] mb-6" />
            <h2 className="font-serif text-2xl sm:text-3xl font-light text-foreground tracking-tight leading-snug">
              What sounds most{' '}
              <span className="italic">like you right now?</span>
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Pathways Grid */}
      <section className="px-6 pb-10">
        <div className="max-w-6xl mx-auto">
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
            data-testid="pathways-grid"
          >
            {PATHWAYS.map((pathway, i) => (
              <PathwayCard
                key={pathway.id}
                pathway={pathway}
                index={i}
                onSelect={onSelectTool}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Emotional CTA — "the searching stops" (tight to grid) */}
      <section className="relative px-6 pt-4 pb-20" data-testid="hub-final-cta">
        <div className="max-w-2xl mx-auto">
          {/* Soft warm backdrop behind the CTA */}
          <div
            className="absolute inset-x-6 top-0 -bottom-6 -z-10 rounded-3xl"
            aria-hidden="true"
            style={{
              background:
                'radial-gradient(ellipse 90% 70% at 50% 50%, hsl(var(--gold) / 0.10), transparent 70%), radial-gradient(ellipse 70% 60% at 80% 30%, hsl(var(--teal) / 0.06), transparent 70%)',
            }}
          />
          <motion.div
            {...fade(0.05)}
            className="text-center pt-4 pb-6 px-4 sm:px-8"
          >
            <h2
              className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-medium text-foreground tracking-tight leading-[1.12] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="cta-headline"
            >
              When you're ready,{' '}
              <span className="italic text-[hsl(var(--gold))]">the searching stops.</span>
            </h2>
            <p className="text-base sm:text-lg text-foreground/75 font-light leading-relaxed max-w-lg mx-auto mb-9">
              Tell HAEVN what you're looking for, and we'll start sending matches that actually make sense.
            </p>

            <a
              href="https://www.haevn.co/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-tell-haevn"
              className="inline-flex items-center gap-2 rounded-full px-7 sm:px-8 py-4 text-sm sm:text-base font-medium bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.92)] transition-all duration-200 hover:scale-[0.98] active:scale-[0.96] shadow-lg shadow-[hsl(var(--gold)/0.25)]"
            >
              Tell HAEVN What You're Looking For
              <ArrowRight className="w-4 h-4" />
            </a>

            <p className="mt-5 text-xs sm:text-sm text-muted-foreground/70 font-light italic">
              Takes about 15 minutes. Then HAEVN takes it from there.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
